import { PageHero, SectionHeading } from "@/components/site/Sections";
import { CtaButton } from "@/components/site/CtaButton";
import { Reveal } from "@/components/site/Reveal";
import { IMAGES } from "@/data/content";
import { Check, AlertTriangle, ExternalLink } from "lucide-react";

const SERVICES = [
  "Property inspection", "Auction preparation", "Deadline tracking", "Confirmation monitoring",
  "Final-payment coordination", "Deed tracking", "Recording follow-up", "Post-purchase inspection",
  "Possession coordination", "Locksmith coordination", "Clean-out", "Contractor coordination", "Financing assistance",
];

// Editable data record — deadlines are NOT hard-coded into copy. Verify before bidding.
const AUCTION_REQUIREMENTS = [
  { field: "County", value: "Northumberland County" },
  { field: "State", value: "Pennsylvania" },
  { field: "Auction Type", value: "Sheriff Sale / Tax Claim Sale (Upset, Judicial, Repository)" },
  { field: "Auction Platform", value: "Verify with official platform" },
  { field: "Deposit Requirement", value: "Contact Vertexhub Ventures to verify current requirements" },
  { field: "Payment Deadline", value: "Contact Vertexhub Ventures to verify current requirements" },
  { field: "Confirmation Process", value: "Contact Vertexhub Ventures to verify current requirements" },
  { field: "Deed Process", value: "Contact Vertexhub Ventures to verify current requirements" },
  { field: "Official Source", value: "Northumberland County Sheriff / Tax Claim Bureau" },
  { field: "Last Verified", value: "Pending verification" },
];

export default function NorthumberlandCounty() {
  return (
    <div>
      <PageHero
        eyebrow="Northumberland County, Pennsylvania"
        title="Buying at a Northumberland County Auction? Let Vertexhub Handle the Local Follow-Through."
        subtitle="Sunbury, Shamokin, Milton and Mount Carmel ground operations for out-of-area and local investors — inspection, deadline tracking, deed follow-up, possession coordination and financing support."
        image={IMAGES.courthouse}
      >
        <CtaButton to="/submit-property" size="lg">Submit a Property</CtaButton>
      </PageHero>

      <section className="py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <SectionHeading eyebrow="What We Coordinate" title="Northumberland County Auction Property Support" />
          <div className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s, i) => (
              <Reveal key={s} delay={i * 40}>
                <div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-700">
                  <Check className="h-4 w-4 shrink-0 text-gold" /> {s}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-slate-200 bg-[#F5F3EC] py-20 lg:py-24">
        <div className="mx-auto max-w-4xl px-4 lg:px-8">
          <SectionHeading eyebrow="Current Auction Requirements" title="Always Verify Before You Bid" subtitle="Auction requirements change. This information is maintained as editable data and must be confirmed with the official source before bidding." />

          <div className="mt-10 overflow-hidden rounded-xl border border-slate-200">
            <table className="w-full text-left text-sm">
              <tbody>
                {AUCTION_REQUIREMENTS.map((r, i) => (
                  <tr key={r.field} className={i % 2 ? "bg-white" : "bg-white"}>
                    <th className="w-1/3 border-b border-slate-200 px-5 py-3 font-mono-x text-xs uppercase tracking-wider text-gold">{r.field}</th>
                    <td className="border-b border-slate-200 px-5 py-3 text-slate-600">{r.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-6 flex items-start gap-3 rounded-xl border border-gold/30 bg-white p-5">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
            <p className="text-sm leading-relaxed text-slate-600">
              Always verify current requirements with the applicable county, Sheriff, Treasurer, court or
              official auction platform before bidding.
            </p>
          </div>

          <div className="mt-6 text-center">
            <a href="https://www.norrycopa.net/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm text-gold hover:underline">
              Northumberland County official resources <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
